import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdminTransactionsFacade,
  BulkTransitionPayloadV4,
  BulkTransitionResultV4,
} from './admin-transactions.facade';
import {
  AdminTransactionListItemV4,
  AdminTransactionsFilterV4,
  AdminTransitionDtoV4,
} from '@models/resources/admin-transaction.model';
import {
  InvoiceLite,
  TransactionRequestV4,
  TransactionStatusV4,
  UpdateInvoiceRecipientDto,
  UpdateInvoiceRecipientResponse,
} from '@models/resources/transaction-request.model';
import {
  DEMO_TRANSACTIONS_V4,
  TX_CUSTOMER_ID_BY_TX_ID,
} from '@data/fixtures/transactions-v4.fixtures';
import { resolveCustomerStub } from '@data/dummy-utils';
import { dummyError, dummyResponse } from '@data/dummy-response.util';

/**
 * Erlaubte V4-Status-Übergänge je Type (ADR-006 §D2 + Sprint 8 Sell-
 * Workflow). Forward-only entlang des Workflows; `rejected`/`cancelled`
 * sind jederzeit erreichbar; alle Endstati erlauben keine weiteren
 * Wechsel.
 */
const ALLOWED_BUY_V4_TRANSITIONS: Record<TransactionStatusV4, readonly TransactionStatusV4[]> = {
  open: ['consulted', 'info_sent', 'rejected', 'cancelled'],
  info_sent: ['consulted', 'rejected', 'cancelled'],
  // Sprint 9 (ADR-011): Kaufauftrag-Phase. consulted → order_sent →
  // contract_signed → invoiced. Forward-Schritte laufen über die
  // Order-Actions; hier für die Dummy-Validierung vollständig abgebildet.
  consulted: ['order_sent', 'rejected', 'cancelled'],
  order_sent: ['contract_signed', 'rejected', 'cancelled'],
  contract_signed: ['invoiced', 'order_sent', 'rejected', 'cancelled'],
  invoiced: ['paid', 'rejected', 'cancelled'],
  paid: ['depot_stored', 'rejected', 'cancelled'],
  depot_stored: [],
  sold: [],
  paid_out: [],
  rejected: [],
  cancelled: [],
};

const ALLOWED_SELL_V4_TRANSITIONS: Record<TransactionStatusV4, readonly TransactionStatusV4[]> = {
  open: ['consulted', 'info_sent', 'rejected', 'cancelled'],
  info_sent: ['consulted', 'rejected', 'cancelled'],
  consulted: ['sold', 'rejected', 'cancelled'],
  sold: ['paid_out', 'rejected', 'cancelled'],
  paid_out: [],
  // Kaufauftrag-Phase existiert im Sell-Pfad nicht.
  order_sent: [],
  contract_signed: [],
  invoiced: [],
  paid: [],
  depot_stored: [],
  rejected: [],
  cancelled: [],
};

function isAllowedV4Transition(
  type: 'buy' | 'sell',
  from: TransactionStatusV4,
  to: TransactionStatusV4,
): boolean {
  const map = type === 'sell' ? ALLOWED_SELL_V4_TRANSITIONS : ALLOWED_BUY_V4_TRANSITIONS;
  return map[from].includes(to);
}

/**
 * Sprint 8 Sell-Workflow Demo-Approximation: in der Dummy-Facade gibt
 * es keinen LBMA-Fix, deshalb berechnen wir die Auszahlungssumme aus
 * der schon bekannten `reference_price` der TX (Snapshot bei Anfrage-
 * Erstellung). Im echten BE rechnet `TransactionTransitionService`
 * mit dem `metal_prices`-Eintrag des `sale_date`.
 */
function computeDummyPayoutEur(grams: number, referencePrice: number | null): number {
  if (referencePrice === null || grams <= 0) return 0;
  return Math.round(grams * referencePrice * 100) / 100;
}

/**
 * In-Memory-V4-Variante (Sprint 6 S6-5).
 *
 * Hält eine mutable Kopie der V4-TX-Fixtures, damit `transition()` und
 * `bulkTransition()` Status-Wechsel für Folge-`list()`-Calls sichtbar
 * machen. Customer-Facade hat eine getrennte Kopie, damit Demo-Sessions
 * von beiden Seiten unabhängig laufen.
 */
@Injectable()
export class DummyAdminTransactionsFacade implements AdminTransactionsFacade {
  private readonly store: TransactionRequestV4[] = DEMO_TRANSACTIONS_V4.map((t) => ({
    ...t,
  }));

  list(filter?: AdminTransactionsFilterV4): Observable<AdminTransactionListItemV4[]> {
    const status = filter?.status ?? 'all';
    const customerId = filter?.customer_id;
    const search = filter?.search?.trim().toLowerCase();
    const type = filter?.type;

    const filtered = this.store.filter((t) => {
      if (type !== undefined && t.type !== type) return false;
      if (status !== 'all' && t.status !== status) return false;
      const cid = TX_CUSTOMER_ID_BY_TX_ID[t.id];
      if (customerId !== undefined && cid !== customerId) return false;
      if (search) {
        const customer = resolveCustomerStub(cid);
        const haystack = [
          String(t.id),
          customer.first_name,
          customer.last_name,
          customer.depot_number,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });

    const enriched = filtered
      .map((t) => this.withCustomer(t))
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    return dummyResponse('admin.transactions', enriched);
  }

  get(id: number): Observable<AdminTransactionListItemV4> {
    const match = this.store.find((t) => t.id === id);
    if (!match) {
      return dummyError('admin.transactions');
    }
    return dummyResponse('admin.transactions', this.withCustomer(match));
  }

  transition(
    id: number,
    dto: AdminTransitionDtoV4,
  ): Observable<AdminTransactionListItemV4> {
    const index = this.store.findIndex((t) => t.id === id);
    if (index === -1) {
      return dummyError('admin.transactions');
    }
    const current = this.store[index];
    if (!isAllowedV4Transition(current.type, current.status, dto.target_status)) {
      return dummyError('admin.transactions');
    }
    // Sprint 8: recipient_override darf nur bei target_status='invoiced'
    // mitgeschickt werden — sonst spiegelt der Dummy das BE-422-Verhalten.
    if (dto.recipient_override && dto.target_status !== 'invoiced') {
      return dummyError('admin.transactions');
    }
    // Sprint 8 Sell-Workflow: `sale_date` ist Pflicht beim
    // `consulted → sold`-Übergang und darf bei anderen Targets nicht
    // mitgeschickt werden (BE-422-Spiegel).
    if (dto.target_status === 'sold' && !dto.sale_date) {
      return dummyError('admin.transactions');
    }
    if (dto.sale_date && dto.target_status !== 'sold') {
      return dummyError('admin.transactions');
    }

    let nextInvoice: InvoiceLite | null = current.invoice;
    if (dto.target_status === 'invoiced') {
      const override = dto.recipient_override ?? null;
      const emailOverride = normalizeOverrideString(override?.recipient_email_override);
      const nameOverride = normalizeOverrideString(override?.recipient_name_override);
      const addressOverride = normalizeAddressOverride(
        override?.recipient_address_override,
      );

      const customer = resolveCustomerStub(TX_CUSTOMER_ID_BY_TX_ID[current.id] ?? 0);
      const effectiveName =
        nameOverride ?? `${customer.first_name} ${customer.last_name}`.trim();

      // Auch wenn schon eine InvoiceLite existiert (z.B. seed): replace
      // mit aktuellen Override-Daten. Für den FE-Dummy ist das idempotent.
      nextInvoice = {
        id: current.invoice?.id ?? current.id,
        invoice_number:
          current.invoice?.invoice_number ??
          `RE-DUMMY-${current.id.toString().padStart(6, '0')}`,
        amount_eur: current.invoice?.amount_eur ?? current.amount_eur ?? 0,
        status: 'sent',
        type: 'transaction',
        generated_at: current.invoice?.generated_at ?? new Date().toISOString(),
        sent_at: new Date().toISOString(),
        paid_at: null,
        download_url:
          current.invoice?.download_url ?? `/api/invoices/${current.id}/download`,
        recipient_email_override: emailOverride,
        recipient_name_override: nameOverride,
        recipient_address_override: addressOverride,
        recipient_pdf_version: current.invoice?.recipient_pdf_version ?? 0,
        effective_recipient: {
          email: emailOverride ?? current.invoice?.effective_recipient.email ?? null,
          name: effectiveName,
          street: addressOverride?.street ?? null,
          zip: addressOverride?.zip ?? null,
          city: addressOverride?.city ?? null,
          country: addressOverride?.country ?? null,
        },
      };
    }

    // Sprint 8 Sell-Workflow: bei `consulted → sold` berechnet das
    // Backend amount_eur aus `requested_grams × LBMA-Fix(sale_date) ×
    // (1 - spread_sell)`. Im Dummy reusen wir `reference_price` als
    // grobe Approximation und persistieren `purchase_date = sale_date`
    // sowie `purchased_grams = -requested_grams` als negative Buchung.
    let nextAmountEur = current.amount_eur;
    let nextPurchaseDate = current.purchase_date;
    let nextPurchasedGrams = current.purchased_grams;
    let nextPurchaseFix = current.purchase_fix_eur_per_gram;

    if (dto.target_status === 'sold' && dto.sale_date) {
      const grams = current.requested_grams ?? 0;
      nextAmountEur = computeDummyPayoutEur(grams, current.reference_price);
      nextPurchaseDate = dto.sale_date;
      nextPurchasedGrams = -grams;
      nextPurchaseFix = current.reference_price;
    }

    const updated: TransactionRequestV4 = {
      ...current,
      status: dto.target_status,
      note: dto.comment?.trim() ? dto.comment.trim() : current.note,
      invoice: nextInvoice,
      amount_eur: nextAmountEur,
      purchase_date: nextPurchaseDate,
      purchased_grams: nextPurchasedGrams,
      purchase_fix_eur_per_gram: nextPurchaseFix,
      updated_at: new Date().toISOString(),
    };
    this.store[index] = updated;
    return dummyResponse('admin.transactions', this.withCustomer(updated));
  }

  updateInvoiceNote(
    id: number,
    invoiceNote: string | null,
  ): Observable<AdminTransactionListItemV4> {
    const index = this.store.findIndex((t) => t.id === id);
    if (index === -1) {
      return dummyError('admin.transactions');
    }
    const current = this.store[index];
    if (current.status !== 'consulted') {
      return dummyError('admin.transactions');
    }
    const trimmed = invoiceNote?.trim();
    const next: TransactionRequestV4 = {
      ...current,
      invoice_note: trimmed ? trimmed : null,
      updated_at: new Date().toISOString(),
    };
    this.store[index] = next;
    return dummyResponse('admin.transactions', this.withCustomer(next));
  }

  bulkTransition(payload: BulkTransitionPayloadV4): Observable<BulkTransitionResultV4> {
    const updated: { id: number; status: TransactionStatusV4 }[] = [];
    const rejected: { id: number; reason: string }[] = [];
    const now = new Date().toISOString();

    for (const id of payload.transaction_ids) {
      const index = this.store.findIndex((t) => t.id === id);
      if (index === -1) {
        rejected.push({ id, reason: 'not_found' });
        continue;
      }
      const current = this.store[index];
      if (!isAllowedV4Transition(current.type, current.status, payload.target_status)) {
        rejected.push({ id, reason: 'invalid_transition' });
        continue;
      }
      const next: TransactionRequestV4 = {
        ...current,
        status: payload.target_status,
        note: payload.comment?.trim() ? payload.comment.trim() : current.note,
        purchase_date:
          payload.target_status === 'depot_stored' && payload.purchase_date
            ? payload.purchase_date
            : current.purchase_date,
        updated_at: now,
      };
      this.store[index] = next;
      updated.push({ id: next.id, status: next.status });
    }

    return dummyResponse('admin.transactions', {
      bulk_op_id: `dummy-${Date.now()}`,
      updated,
      rejected,
    });
  }

  updateInvoiceRecipient(
    invoiceId: number,
    payload: UpdateInvoiceRecipientDto,
  ): Observable<UpdateInvoiceRecipientResponse> {
    const index = this.store.findIndex((t) => t.invoice?.id === invoiceId);
    if (index === -1) {
      return dummyError('admin.transactions');
    }
    const current = this.store[index];
    const inv = current.invoice;
    if (inv === null) {
      return dummyError('admin.transactions');
    }

    const emailOverride = normalizeOverrideString(payload.recipient_email_override);
    const nameOverride = normalizeOverrideString(payload.recipient_name_override);
    const addressOverride = normalizeAddressOverride(payload.recipient_address_override);

    const customer = resolveCustomerStub(TX_CUSTOMER_ID_BY_TX_ID[current.id] ?? 0);
    const effectiveName =
      nameOverride ?? `${customer.first_name} ${customer.last_name}`.trim();

    const updatedInvoice: InvoiceLite = {
      ...inv,
      recipient_email_override: emailOverride,
      recipient_name_override: nameOverride,
      recipient_address_override: addressOverride,
      recipient_pdf_version:
        inv.sent_at !== null && hasAnyOverride(emailOverride, nameOverride, addressOverride)
          ? inv.recipient_pdf_version + 1
          : inv.recipient_pdf_version,
      effective_recipient: {
        // Dummy-Stub hat keine Customer-E-Mail — Fallback auf den
        // bisherigen `effective_recipient.email`, damit das FE nicht
        // einen lokalen Override fälschlich wegrendert wenn der Caller
        // ihn zurücksetzt.
        email: emailOverride ?? inv.effective_recipient.email,
        name: effectiveName,
        street: addressOverride?.street ?? null,
        zip: addressOverride?.zip ?? null,
        city: addressOverride?.city ?? null,
        country: addressOverride?.country ?? null,
      },
    };

    this.store[index] = {
      ...current,
      invoice: updatedInvoice,
      updated_at: new Date().toISOString(),
    };

    return dummyResponse('admin.transactions', {
      id: invoiceId,
      invoice_number: inv.invoice_number,
      recipient_email_override: emailOverride,
      recipient_name_override: nameOverride,
      recipient_address_override: addressOverride,
      recipient_pdf_version: updatedInvoice.recipient_pdf_version,
      effective: updatedInvoice.effective_recipient,
    });
  }

  private withCustomer(t: TransactionRequestV4): AdminTransactionListItemV4 {
    const customer_id = TX_CUSTOMER_ID_BY_TX_ID[t.id] ?? 0;
    // Demo-TXs tragen keinen Kaufauftrag; das Admin-Modell verengt
    // `purchase_order` auf den vollen PurchaseOrder-Typ (Summary aus der
    // Basis ist hier undefined) — Cast ist demo-ehrlich.
    return {
      ...t,
      customer_id,
      customer: resolveCustomerStub(customer_id),
    } as AdminTransactionListItemV4;
  }
}

function normalizeOverrideString(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function normalizeAddressOverride(
  value: UpdateInvoiceRecipientDto['recipient_address_override'],
): InvoiceLite['recipient_address_override'] {
  if (!value) return null;
  const street = normalizeOverrideString(value.street ?? null);
  const zip = normalizeOverrideString(value.zip ?? null);
  const city = normalizeOverrideString(value.city ?? null);
  const country = normalizeOverrideString(value.country ?? null);
  if (street === null && zip === null && city === null && country === null) {
    return null;
  }
  return { street, zip, city, country };
}

function hasAnyOverride(
  email: string | null,
  name: string | null,
  address: InvoiceLite['recipient_address_override'],
): boolean {
  return email !== null || name !== null || address !== null;
}
